import { Badge, Card, Group, SimpleGrid, Stack, Text } from '@mantine/core';
import { IconMeeple } from '@tabler/icons-react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { DEMO_CATALOG_COPY } from '@creativitas/shared';
import { LockedOverlay } from '@/components/domain/LockedOverlay';
import { AssetImage } from '@/components/ui/AssetImage';
import { PageHeader } from '@/components/ui/PageHeader';
import { CardGridSkeleton, EmptyState, ErrorState } from '@/components/ui/StateViews';
import { CatalogLockedOffer } from '@/features/access/PlanOffer';
import { lockedCopy } from '@/features/content/accessCopy';
import { fetchGames, GAMES_KEY } from './api';

export function GamesListPage() {
  const navigate = useNavigate();
  const query = useQuery({ queryKey: GAMES_KEY, queryFn: fetchGames });
  const items = query.data?.items ?? [];

  return (
    <>
      <PageHeader title="Игры" description="Игры с ведущим и участниками по ссылке или самостоятельной практики." />
      {query.isLoading ? <CardGridSkeleton count={2} /> : query.isError ? (
        <ErrorState onRetry={() => void query.refetch()} />
      ) : items.length === 0 ? (
        <EmptyState icon={<IconMeeple size={28} />} title="Игр пока нет" />
      ) : (
        <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }} spacing="lg">
          {items.map((game) => {
            const locked = lockedCopy(game.access);
            const card = (
              <Card
                withBorder
                padding="md"
                h="100%"
                onClick={locked ? undefined : () => void navigate(`/games/${game.slug}`)}
                style={{ borderColor: 'var(--border-subtle)', cursor: locked ? 'default' : 'pointer' }}
              >
                <Card.Section>
                  <AssetImage src={game.coverImage} alt={game.title} ratio="1 / 1" fallbackLabel="Поле не загружено" />
                </Card.Section>
                <Stack gap="xs" mt="md">
                  {game.demo ? (
                    /*
                      До запуска демо честно говорит, что открыто три сектора
                      из шести, — а не выдаёт себя за полный круг.
                    */
                    <Group gap={6}>
                      <Badge color="gold" w="fit-content">{DEMO_CATALOG_COPY.badge}</Badge>
                      <Badge variant="light" w="fit-content">
                        {DEMO_CATALOG_COPY.sectors(game.demo.routeSectorIds.length, game.demo.totalSectors)}
                      </Badge>
                    </Group>
                  ) : (
                    <Badge variant="default" w="fit-content">Игра с ведущим</Badge>
                  )}
                  <Text fw={600} size="lg">{game.title}</Text>
                  <Text size="sm" c="var(--text-muted)" lineClamp={3}>{game.description}</Text>
                </Stack>
              </Card>
            );
            return locked ? (
              <LockedOverlay
                key={game.id}
                reason={locked.reason}
                hint={locked.hint ?? undefined}
                onOpen={() => void navigate(`/games/${game.slug}`)}
              >
                {card}
              </LockedOverlay>
            ) : <div key={game.id}>{card}</div>;
          })}
        </SimpleGrid>
      )}
      <CatalogLockedOffer items={items.map((game) => game.access)} />
    </>
  );
}
